import { Box } from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import CharacterCount from "@tiptap/extension-character-count";
import TextAlign from "@tiptap/extension-text-align";

import { BannerType, TipTapNode } from "./interfaces";
import {
  getBannerBackgroundColor,
  getBannerTextColor,
  isValidLinkHref,
  MAX_CHARACTERS,
} from "./utils";
import EditorToolbar from "./Editor/EditorToolbar";

const TipTapEditor = ({
  banner,
  content,
  editable = false,
  onContentChange,
}: {
  banner: BannerType;
  content?: TipTapNode;
  editable?: boolean;
  onContentChange?: (content: TipTapNode) => void;
}) => {
  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({
        strike: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
        codeBlock: false,
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
        shouldAutoLink: (href) => isValidLinkHref(href),
        validate: (href) => isValidLinkHref(href),
      }),
      CharacterCount.configure({ limit: MAX_CHARACTERS }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right", "justify"],
      }),
    ],
    editorProps: {
      attributes: {
        class: "tiptap--container",
      },
    },
    content: content,
    onBlur: editable
      ? ({ editor: e }) => {
          onContentChange?.(e.getJSON() as TipTapNode);
        }
      : undefined,
  });

  return (
    <>
      {editable && editor && <EditorToolbar editor={editor} />}
      <Box
        sx={(theme) => ({
          "& .tiptap--container": {
            width: "initial",
            borderRadius: "12px",
            boxShadow: theme.vars.palette.shadow.main,
            py: 1,
            px: 4.5,
            backgroundColor: getBannerBackgroundColor(theme, banner.criticity),
            background: getBannerBackgroundColor(theme, banner.criticity),
            color: getBannerTextColor(theme, banner.criticity),
          },
          "& p, & h1, & h2, & h3, & h4, & h5, & h6, & a": {
            margin: 0,
            color: getBannerTextColor(theme, banner.criticity),
          },
        })}
      >
        <EditorContent editor={editor} />
      </Box>
    </>
  );
};

export default TipTapEditor;
